import React from 'react';

/* ==================================================================
   Skeletons
   ------------------------------------------------------------------
   Low-contrast placeholders that hold the real layout, so content
   landing does not shove the page around. Used instead of a
   full-page spinner everywhere a shape is predictable.
   ================================================================== */

export function Skeleton({ w = '100%', h = 12, r = 6, style, className = '' }) {
  return (
    <span
      className={`ui-skel ${className}`.trim()}
      aria-hidden="true"
      style={{ display: 'block', width: w, height: h, borderRadius: r, ...style }}
    />
  );
}

/** Opportunity / job card placeholder. */
export function SkeletonJobCard() {
  return (
    <div className="ui-skel-card" aria-hidden="true">
      <div className="ui-skel-row">
        <Skeleton w={40} h={40} r={10} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
          <Skeleton w="65%" h={13} />
          <Skeleton w="40%" h={10} />
        </div>
        <Skeleton w={62} h={22} r={8} />
      </div>
      <div className="ui-skel-row" style={{ gap: 6 }}>
        <Skeleton w={64} h={18} r={20} />
        <Skeleton w={52} h={18} r={20} />
        <Skeleton w={72} h={18} r={20} />
      </div>
      <Skeleton w="45%" h={16} />
      <Skeleton w="100%" h={46} r={10} />
      <div className="ui-skel-row" style={{ justifyContent: 'space-between', paddingTop: 8 }}>
        <Skeleton w={70} h={10} />
        <Skeleton w={96} h={28} r={8} />
      </div>
    </div>
  );
}

/** Candidate card placeholder for talent search results. */
export function SkeletonCandidateCard() {
  return (
    <div className="ui-skel-card" aria-hidden="true">
      <div className="ui-skel-row">
        <Skeleton w={44} h={44} r={12} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
          <Skeleton w="55%" h={13} />
          <Skeleton w="72%" h={10} />
        </div>
      </div>
      <Skeleton w="88%" h={10} />
      <div className="ui-skel-row" style={{ gap: 6 }}>
        <Skeleton w={58} h={18} r={20} />
        <Skeleton w={70} h={18} r={20} />
        <Skeleton w={46} h={18} r={20} />
      </div>
      <Skeleton w="100%" h={30} r={8} />
    </div>
  );
}

/** A row of metric tiles (dashboard summaries). */
export function SkeletonMetrics({ count = 4 }) {
  return (
    <div
      aria-hidden="true"
      style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(150px, 1fr))`, gap: 12 }}
    >
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="ui-skel-card" style={{ padding: 16, gap: 10 }}>
          <Skeleton w={26} h={26} r={8} />
          <Skeleton w="45%" h={20} />
          <Skeleton w="70%" h={9} />
        </div>
      ))}
    </div>
  );
}

/** Generic vertical list of rows (applications, jobs, messages). */
export function SkeletonList({ count = 3, height = 62 }) {
  return (
    <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} h={height} r={12} />
      ))}
    </div>
  );
}

/** A responsive grid of card skeletons. */
export function SkeletonGrid({ count = 4, min = 300, Card = SkeletonJobCard }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 16 }}>
      {Array.from({ length: count }, (_, i) => <Card key={i} />)}
    </div>
  );
}

export default Skeleton;
